/**
 * PayPalCallbackPage - Handles PayPal OAuth callback after user approval
 *
 * This page receives the approval_token_id from PayPal and completes
 * the payment method setup by calling the backend.
 */

import { useEffect, useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getApiUrl } from '../api/client'
import { AuthLayout } from '../components/layout/AuthLayout'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react'

// API_URL is computed dynamically via getApiUrl() for org-scoped subdomains

export function PayPalCallbackPage() {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [returnUrl, setReturnUrl] = useState<string | null>(null)
  const completingRef = useRef(false) // Prevent double-calls from React StrictMode

  const success = searchParams.get('success') === 'true'
  const cancelled = searchParams.get('cancelled') === 'true'
  const approvalTokenId = searchParams.get('approval_token_id')

  useEffect(() => {
    // Get the return URL from sessionStorage (stored before PayPal redirect)
    const storedReturnUrl = sessionStorage.getItem('paypal_return_url')
    if (storedReturnUrl) {
      setReturnUrl(storedReturnUrl)
    }

    if (cancelled) {
      setStatus('error')
      setError('PayPal setup was cancelled')
      return
    }

    if (success && approvalTokenId && !completingRef.current) {
      completingRef.current = true
      completeSetup(approvalTokenId)
    } else if (!success && !cancelled) {
      setStatus('error')
      setError('Invalid callback - missing parameters')
    }
  }, [success, cancelled, approvalTokenId])

  async function completeSetup(tokenId: string) {
    try {
      const response = await fetch(`${getApiUrl()}/api/me/billing/paypal/complete?setup_token=${tokenId}`, {
        method: 'POST',
        credentials: 'include',
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || 'Failed to complete PayPal setup')
      }

      setStatus('success')
      // Clear the stored return URL on success
      sessionStorage.removeItem('paypal_return_url')
    } catch (err: any) {
      setStatus('error')
      setError(err.message || 'Failed to complete PayPal setup')
    }
  }

  function getTargetOrigin(): string {
    // Get origin from stored return URL
    if (returnUrl) {
      try {
        return new URL(returnUrl).origin
      } catch {
        // Invalid URL, fall through
      }
    }
    // If no valid return URL, don't send message to prevent security issues
    return ''
  }

  function handleContinue() {
    if (returnUrl) {
      // Redirect back to the agreement page
      window.location.href = returnUrl
    } else {
      // Fallback: close if in popup, otherwise go to billing
      if (window.opener) {
        const targetOrigin = getTargetOrigin()
        if (targetOrigin) {
          window.opener.postMessage({ type: 'paypal_callback', success: status === 'success' }, targetOrigin)
        }
        window.close()
      } else {
        window.location.href = '/billing'
      }
    }
  }

  function handleClose() {
    // Clear storage and redirect home or close
    sessionStorage.removeItem('paypal_return_url')
    if (window.opener) {
      const targetOrigin = getTargetOrigin()
      if (targetOrigin) {
        window.opener.postMessage({ type: 'paypal_callback', success: false, error: error || 'cancelled' }, targetOrigin)
      }
      window.close()
    } else {
      window.location.href = '/'
    }
  }

  return (
    <AuthLayout>
      <Card className="p-8">
        {status === 'loading' && (
          <div className="text-center">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-[var(--text-primary)]" />
            <h2 className="mt-4 text-lg font-semibold text-[var(--text-primary)]">
              Completing PayPal Setup...
            </h2>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              Please wait while we save your payment method.
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-[var(--success)]" />
            <h2 className="mt-4 text-lg font-semibold text-[var(--text-primary)]">
              PayPal Connected!
            </h2>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              Your PayPal account has been added as a payment method.
            </p>
            <div className="mt-6">
              <Button onClick={handleContinue} className="w-full">
                {returnUrl ? (
                  <>
                    Continue to Agreement
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                ) : (
                  'Close'
                )}
              </Button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <XCircle className="mx-auto h-12 w-12 text-[var(--error)]" />
            <h2 className="mt-4 text-lg font-semibold text-[var(--text-primary)]">
              Setup Failed
            </h2>
            <p className="mt-2 text-sm text-[var(--error)]">
              {error}
            </p>
            <div className="mt-6 flex gap-3">
              {returnUrl && (
                <Button variant="outline" onClick={handleContinue} className="flex-1">
                  Try Again
                </Button>
              )}
              <Button variant="outline" onClick={handleClose} className={returnUrl ? 'flex-1' : 'w-full'}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Card>
    </AuthLayout>
  )
}
